import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMatch, useResolvedPath } from "react-router-dom";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import BarChartIcon from "@mui/icons-material/BarChart";
import CallIcon from "@mui/icons-material/Call";
import CampaignIcon from "@mui/icons-material/Campaign";
import ChatIcon from "@mui/icons-material/Chat";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ContactEmergencyIcon from "@mui/icons-material/ContactEmergency";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FaxIcon from "@mui/icons-material/Fax";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StarIcon from "@mui/icons-material/Star";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import TodayIcon from "@mui/icons-material/Today";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";

import { intersection } from "lodash";

import { useAppSelector } from "app/hooks";
import logo from "assets/icons/suno-logo-white.svg";
import { QBSyncIcon } from "components/icons";
import SupaDemoButton from "components/supa-demo-button";
import {
  CLEARTONE_RESTRICTIONS,
  COLLECTIONS_FEATURE_ENABLED,
  DASHBOARD_FEATURE_FLAG,
  DASHBOARD_WIDGETS_FEATURE,
  DISABLE_REPORTS_AND_DASHBOARD,
  ERA_CLAIMS_TAB_FEATURE,
  FAXES_PORTAL_FEATURE,
  GOOGLE_REVIEW_CONTROL_CENTER,
  HOME_SCREEN_FEATURE,
  LEADS_UI,
  MARKETING_AUTOMATION_FEATURE,
  MARKETPLACE_FEATURE,
  MEDICAL_SCRIBE_FEATURE,
  SUNO_COMMS_FEATURE,
  VERTICAL_SOUTHWESTERN_LIMITATIONS,
  VOB_REQUEST_FEATURE
} from "constants/feature-flags";
import { VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS } from "constants/permissions";
import {
  ADMIN_LEVEL_GROUPS,
  GROUP_ADMINISTRATORS,
  GROUP_AUDIOLOGISTS,
  GROUP_BUSINESS_OWNERS,
  GROUP_SUNO
} from "constants/staff";
import {
  SUPADEMO_GUIDED_TOUR_ID,
  SUPADEMO_SETTINGS_DEMO_ID
} from "constants/supa-demo";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegionClinicsIds,
  selectCurrentUser,
  selectHasRegions
} from "features/auth/auth-slice";
import { useGlobalChat } from "features/chat/use-global-chat.hook";
import { getIncludeMessagesFromOtherClinicsState } from "features/inbox/inbox-slice";
import useDeferredReady from "hooks/use-deferred-ready.hook";
import useIsMobile from "hooks/use-is-mobile.hook";
import useTouchpointsCount from "hooks/use-touchpoints-count";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetUnreadFaxCountQuery } from "services/clinics";
import { useGetUnreadMessagesCountQuery } from "services/messages";
import { useGetMySendBirdDetailsQuery } from "services/users";
import {
  hasAccessRestrictions,
  hasRequiredAccess
} from "utils/auth-access.utils";

import SideMenuItem from "./side-menu-item.component";

const CLEARTONE_RESTRICTED_PATHS = ["/inventory", "/billing", "/eras"];

const layoutContainerSx = {
  justifyContent: "flex-start",
  alignItems: "center",
  pt: 1,
  px: 2
};

export const MENU = [
  {
    to: "/dashboard",
    primary: "Dashboard",
    Icon: DashboardIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS],
    featureFlag: DASHBOARD_FEATURE_FLAG,
    hideFlag: DISABLE_REPORTS_AND_DASHBOARD,
    tooltip: "View key Practice metrics & summaries"
  },
  {
    to: "/inbox",
    primary: "Inbox",
    Icon: QuestionAnswerIcon,
    isFallbackPath: false,
    tooltip: "Manage SMS communications with patients"
  },
  {
    to: "/chat",
    primary: "Chat",
    Icon: ChatIcon,
    isFallbackPath: false,
    tooltip: "In-App Chat"
  },
  {
    to: "/scheduling",
    primary: "Scheduling",
    Icon: TodayIcon,
    isFallbackPath: true,
    tooltip: "Schedule & manage patient appointments"
  },
  {
    to: "/patients",
    primary: "Patients",
    Icon: PeopleAltIcon,
    isFallbackPath: false,
    tooltip: "Access & update patients’ medical & personal details"
  },
  {
    to: "/billing",
    primary: "Billing",
    Icon: RequestQuoteOutlinedIcon,
    isFallbackPath: false,
    tooltip: "Manage invoices & payment details"
  },
  {
    to: "/collections",
    primary: "Collections",
    Icon: AccountBalanceWalletIcon,
    isFallbackPath: false,
    featureFlag: COLLECTIONS_FEATURE_ENABLED,
    tooltip: "Manage outstanding patient balances & payment recovery"
  },
  {
    to: "/eras",
    primary: "ERAs",
    Icon: SummarizeOutlinedIcon,
    isFallbackPath: false,
    roles: ADMIN_LEVEL_GROUPS,
    featureFlag: ERA_CLAIMS_TAB_FEATURE,
    tooltip: "View & manage insurance claims & payments"
  },
  {
    to: "/vobs",
    primary: "VOBs",
    Icon: AssignmentIcon,
    isFallbackPath: false,
    roles: ADMIN_LEVEL_GROUPS,
    featureFlag: VOB_REQUEST_FEATURE,
    tooltip: "View Verification of Benefits requests"
  },
  {
    to: "/reports",
    primary: "Reports",
    Icon: BarChartIcon,
    isFallbackPath: false,
    roles: ADMIN_LEVEL_GROUPS,
    permissions: [VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS],
    hideFlag: DISABLE_REPORTS_AND_DASHBOARD,
    tooltip: "View & generate detailed reports"
  },
  {
    to: "/marketing",
    primary: "Marketing",
    Icon: CampaignIcon,
    isFallbackPath: false,
    roles: ADMIN_LEVEL_GROUPS,
    featureFlag: MARKETING_AUTOMATION_FEATURE,
    tooltip: "Plan & track campaigns & patient touchpoints"
  },
  {
    to: "/reviews",
    primary: "Reviews",
    Icon: StarIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS, GROUP_ADMINISTRATORS, GROUP_AUDIOLOGISTS],
    featureFlag: GOOGLE_REVIEW_CONTROL_CENTER,
    tooltip: "Manage Google reviews"
  },
  {
    to: "/medical-scribes",
    primary: "Scribes",
    Icon: AudioFileIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS, GROUP_SUNO, GROUP_AUDIOLOGISTS],
    featureFlag: MEDICAL_SCRIBE_FEATURE,
    tooltip: "Medical Scribes"
  },

  {
    to: "/calls",
    primary: "Calls",
    Icon: CallIcon,
    isFallbackPath: false,
    featureFlag: SUNO_COMMS_FEATURE,
    tooltip: "Log and manage patient calls & follow-ups"
  },

  {
    to: "/leads",
    primary: "Leads",
    Icon: ContactEmergencyIcon,
    isFallbackPath: false,
    tooltip: "Suno Leads Application",
    featureFlag: LEADS_UI
  },

  {
    to: "/faxes-portal",
    primary: "Faxes",
    Icon: FaxIcon,
    isFallbackPath: false,
    featureFlag: FAXES_PORTAL_FEATURE,
    tooltip: "View and manage faxes"
  },
  {
    to: "/inventory",
    primary: "Inventory",
    Icon: ContentPasteSearchIcon,
    isFallbackPath: false,
    tooltip: "Manage & track device/accessory status, orders, & stock"
  },
  {
    to: "/marketplace",
    primary: "Marketplace",
    Icon: StorefrontIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS],
    featureFlag: MARKETPLACE_FEATURE,
    tooltip: "Browse & enable integrations",
    divider: true
  },
  {
    to: "/settings",
    primary: "Settings",
    Icon: SettingsOutlinedIcon,
    isFallbackPath: false,
    tooltip: "Customize clinic preferences & user settings",
    supaDemoId: SUPADEMO_SETTINGS_DEMO_ID
  },

  {
    to: "/quickbooks",
    primary: "QuickBooks",
    Icon: QBSyncIcon as any,
    isFallbackPath: false,
    tooltip: "QuickBooks settings"
  }
];

export default function LayoutSideMenu() {
  const theme = useTheme();
  const flags = useFlags();
  const isMobile = useIsMobile();
  const currentUser = useAppSelector(selectCurrentUser);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);
  const includeMessagesFromOtherClinics = useSelector(
    getIncludeMessagesFromOtherClinicsState
  );
  const isCleartoneRestrictionsEnabled = flags[CLEARTONE_RESTRICTIONS];
  const verticalSouthwesternLimitationsEnabled =
    flags[VERTICAL_SOUTHWESTERN_LIMITATIONS];
  const { assignedTouchpointsCount } = useTouchpointsCount();

  let resolved = useResolvedPath("/");
  const isDefaultPath = Boolean(
    useMatch({ path: resolved.pathname, end: true })
  );

  const [showMenu, setShowMenu] = useState(!isMobile);

  useEffect(() => {
    setShowMenu(!isMobile);
  }, [isMobile]);

  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const { unreadCount } = useGlobalChat();
  const deferredReady = useDeferredReady();
  const { data: unreadMessagesCount } = useGetUnreadMessagesCountQuery(
    {
      clinicId: includeMessagesFromOtherClinics ? undefined : currentClinic?.id,
      clinics:
        includeMessagesFromOtherClinics && hasRegions
          ? currentRegionClinicsIds
          : undefined
    },
    {
      pollingInterval: 3600000,
      skip: !deferredReady || !currentClinic?.id
    }
  );

  const { data: unreadFaxCount } = useGetUnreadFaxCountQuery(
    { clinicId: currentClinic?.id! },
    {
      pollingInterval: 600000,
      skip: !deferredReady || !currentClinic?.id
    }
  );
  const { data: sendbirdDetails } = useGetMySendBirdDetailsQuery(undefined, {
    skip: !currentUser?.sendbird_user_id
  });

  const {
    userRoles,
    userPermissions,
    isScheduler,
    canAccessDashboards,
    canAccessQBO,
    canAccessCampaigns
  } = useUserRoles();

  const isQuickbooksEnabled = Boolean(
    currentPractice?.is_quickbooks_enabled && canAccessQBO
  );

  const isHomeScreenEnabled = flags[HOME_SCREEN_FEATURE] ?? false;
  const isDashboardWidgetsEnabled =
    (flags[DASHBOARD_WIDGETS_FEATURE] ?? false) &&
    Boolean(currentPractice?.enable_dashboard_widgets);
  const showHomeItem = isHomeScreenEnabled || isDashboardWidgetsEnabled;

  const Menu = useMemo(() => {
    const homeItem = showHomeItem
      ? [
          {
            to: "/home",
            primary: "Home",
            Icon: HomeIcon,
            isFallbackPath: false,
            tooltip: "Home"
          }
        ]
      : [];

    const items = [...homeItem, ...MENU].filter(
      ({ roles, permissions, featureFlag, hideFlag, to }) => {
        if (isScheduler) {
          const allowedPaths = [
            "/home",
            "/scheduling",
            "/patients",
            "/settings"
          ];
          return allowedPaths.includes(to);
        }

        if (to === "/quickbooks") {
          return isQuickbooksEnabled;
        }
        if (to === "/dashboard") {
          return (
            canAccessDashboards &&
            (!featureFlag || flags[featureFlag]) &&
            (!hideFlag || !flags[hideFlag])
          );
        }
        if (to === "/marketing") {
          return (
            (canAccessCampaigns ||
              (currentUser &&
                intersection(roles || [], userRoles).length > 0)) &&
            (!featureFlag || flags[featureFlag])
          );
        }
        if (to === "/chat") {
          return Boolean(
            currentPractice?.sendbird_chat_enabled &&
              currentUser?.sendbird_user_id &&
              sendbirdDetails?.access_token
          );
        }
        if (to === "/collections") {
          return Boolean(
            flags[COLLECTIONS_FEATURE_ENABLED] &&
              currentPractice?.enrolled_in_collections &&
              currentUser?.has_access_to_collections
          );
        }
        if (to === "/vobs") {
          return Boolean(
            flags[VOB_REQUEST_FEATURE] &&
              (currentClinic?.is_claim_md_account_key_set ||
                currentPractice?.is_claim_md_account_key_set)
          );
        }
        if (
          CLEARTONE_RESTRICTED_PATHS.includes(to) &&
          isCleartoneRestrictionsEnabled
        ) {
          return (
            currentUser &&
            intersection(ADMIN_LEVEL_GROUPS, userRoles).length > 0
          );
        }
        if (to === "/faxes-portal" && verticalSouthwesternLimitationsEnabled) {
          return (
            currentUser &&
            intersection(ADMIN_LEVEL_GROUPS, userRoles).length > 0
          );
        }
        return (
          (!featureFlag || flags[featureFlag]) &&
          (!hideFlag || !flags[hideFlag]) &&
          (!hasAccessRestrictions(roles, permissions) ||
            hasRequiredAccess(
              currentUser,
              userRoles,
              userPermissions,
              roles,
              permissions
            ))
        );
      }
    );
    return items.map((menuItem, index) => {
      if (menuItem.primary === "Inbox") {
        return {
          ...menuItem,
          badgeContent: unreadMessagesCount || 0
        };
      }

      if (menuItem.primary === "Chat") {
        return {
          ...menuItem,
          badgeContent: unreadCount || 0
        };
      }

      if (menuItem.primary === "Marketing") {
        return { ...menuItem, badgeContent: assignedTouchpointsCount };
      }

      if (menuItem.primary === "Faxes") {
        return {
          ...menuItem,
          badgeContent: unreadFaxCount?.count || 0
        };
      }

      if (index === items.length - 1) {
        return { ...menuItem, sx: { mb: 15 } };
      } else {
        return menuItem;
      }
    });
  }, [
    isScheduler,
    isQuickbooksEnabled,
    showHomeItem,
    canAccessDashboards,
    canAccessCampaigns,
    isCleartoneRestrictionsEnabled,
    verticalSouthwesternLimitationsEnabled,
    flags,
    currentUser,
    userRoles,
    userPermissions,
    currentPractice,
    sendbirdDetails?.access_token,
    currentClinic?.is_claim_md_account_key_set,
    unreadMessagesCount,
    unreadCount,
    assignedTouchpointsCount,
    unreadFaxCount?.count
  ]);

  const layoutSx = useMemo(() => {
    return {
      width: "100px",
      height: "100vh",
      overflow: "auto",
      background: theme.palette.sideMenu.main,
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none"
      },
      display: showMenu ? "block" : "none",
      position: isMobile ? "absolute" : "relative",
      zIndex: 3,
      boxShadow: isMobile ? "20px 0 15px -12px rgba(0, 0, 0, 0.7)" : "none"
    };
  }, [theme.palette.sideMenu.main, isMobile, showMenu]);

  const menuItems = useMemo(() => {
    return Menu.map(({ to, tooltip, ...props }, index) => (
      <SideMenuItem
        key={`${index}_${to}`}
        isDefaultPath={isDefaultPath}
        to={to}
        tooltip={tooltip}
        {...props}
      />
    ));
  }, [Menu, isDefaultPath]);

  if (isMobile && !showMenu) {
    return (
      <Box
        sx={{
          position: "absolute",
          left: 2,
          top: 2
        }}
      >
        <Button
          sx={{
            background: theme.palette.sideMenu.main,
            color: "white",
            minWidth: "38px",
            minHeight: "38px",
            boxShadow: "2px 2px 7px 2px rgba(0, 0, 0, 0.4)"
          }}
          style={{ padding: 0 }}
          size="small"
          onClick={() => setShowMenu(true)}
        >
          <MenuIcon sx={{ width: "27px", height: "27px" }} />
        </Button>
      </Box>
    );
  }
  return (
    <ClickAwayListener
      onClickAway={() => {
        if (isMobile) {
          setShowMenu(false);
        }
      }}
    >
      <Box>
        {isMobile && (
          <IconButton
            size="small"
            sx={{
              color: "white",
              position: "absolute",
              left: "94px",
              top: "3px",
              background: theme.palette.sideMenu.main,
              border: `solid 1px white`,
              zIndex: 100
            }}
            onClick={() => setShowMenu(false)}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        <Stack spacing={1} sx={layoutSx} data-testid="layout-side-menu">
          <Stack sx={layoutContainerSx}>
            <Box
              component="img"
              src={logo}
              alt="logo"
              maxWidth={theme.typography.pxToRem(80)}
            />
            <SupaDemoButton
              isSideMenu
              supaDemoId={SUPADEMO_GUIDED_TOUR_ID}
              tooltip="Guided Tour"
              label="guided-tour"
              sx={{ height: "40px", mt: 1, mb: -1 }}
            />
          </Stack>
          <List>{menuItems}</List>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
}
